// components/Toolbar.js

import React, { useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar as MuiToolbar, Typography, Button, Popover, Box, Slider, TextField, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Snackbar, Alert } from '@mui/material';
import { Description as DescriptionIcon, PictureAsPdf as PdfIcon, Home as HomeIcon, ViewInAr as ViewInArIcon, ListAlt as ListAltIcon, KeyboardAlt as KeyboardAltIcon, Mouse as MouseIcon, TouchApp as TouchAppIcon, Layers as LayersIcon, ZoomIn as ZoomInIcon, Draw as DrawIcon, Straighten as StraightenIcon, MenuBook as MenuBookIcon, Send as SendIcon } from '@mui/icons-material';
import { getManualLabel, openUserManual } from '../utils/userManual';

const KbdKey = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 0.75,
      py: 0.2,
      borderRadius: '0.3rem',
      background: '#fff',
      border: '0.0625rem solid #d0d0d0',
      borderBottom: '0.175rem solid #b0b0b0',
      fontFamily: 'monospace',
      fontSize: '0.7rem',
      fontWeight: 700,
      color: '#212121',
      whiteSpace: 'nowrap',
      lineHeight: 1.4,
      boxShadow: '0 0.0625rem 0.125rem rgba(0,0,0,0.08)',
    }}
  >
    {children}
  </Box>
);

const ControlRow = ({ keys, desc }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.65, borderBottom: '0.0625rem solid #f0f0f0' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: '9rem', flexWrap: 'wrap' }}>
      {keys.map((k, i) => (
        <React.Fragment key={i}>
          <KbdKey>{k}</KbdKey>
          {i < keys.length - 1 && (
            <Typography component="span" sx={{ fontSize: '0.65rem', color: '#bdbdbd', lineHeight: 1 }}>+</Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
    <Typography sx={{ fontSize: '0.75rem', color: '#616161', lineHeight: 1.3, flexGrow: 1, textAlign: 'right' }}>
      {desc}
    </Typography>
  </Box>
);

const ControlSection = ({ icon, title, color, children }) => (
  <Box sx={{
    background: '#fff',
    border: '0.0625rem solid #e8e8e8',
    borderRadius: '0.625rem',
    overflow: 'hidden',
    boxShadow: '0 0.0625rem 0.25rem rgba(0,0,0,0.05)',
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.75, py: 1.1,
      background: `linear-gradient(90deg, ${color}14 0%, transparent 100%)`,
      borderBottom: '0.0625rem solid #eeeeee',
    }}>
      <Box sx={{ color, display: 'flex', alignItems: 'center', '& svg': { fontSize: '1rem' } }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#212121', letterSpacing: '0.02em' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 1.75, py: 0.5, '& > :last-child': { borderBottom: 'none' } }}>
      {children}
    </Box>
  </Box>
);

const Toolbar = ({
  zoom,
  setZoom,
  textZoom,
  setTextZoom,
  positionTextZoom,
  setPositionTextZoom,
  onApplyLinearPattern,
  onOpenPrice,
  onNewDrawing,
  onSaveDrawing,
  onSaveAsPdf,
  onSaveAsDwg,
  onSaveSpecification,
  onSaveAsIGES,
  onSaveAsSTEP,
  onSaveAsDXF,
  onSaveCuttingModels,
  onLoadDrawing,
  onGoHome,
  onBackToAutoConstructor,
  onSaveForWorkshop,
  workshopInfo,
  onSendToWorkshop,
  isAdmin,
  userId,
  isLoggedIn,
  onRequestQuote,
}) => {
  const controlsSeenKey = userId ? `cad_controls_seen_${userId}` : 'cad_controls_seen';
  const alreadySeen = localStorage.getItem(controlsSeenKey) === 'true';

  const [anchorEl, setAnchorEl] = useState(null);
  const [patternAnchorEl, setPatternAnchorEl] = useState(null);
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const [patternCount, setPatternCount] = useState(2);
  const [patternDx, setPatternDx] = useState(0);
  const [patternDy, setPatternDy] = useState(0);
  const [controlsOpen, setControlsOpen] = useState(!alreadySeen);
  const [controlsHighlight, setControlsHighlight] = useState(true);
  const [guestSnackbar, setGuestSnackbar] = useState(false);
  const highlightTimerRef = useRef(null);

  useEffect(() => {
    highlightTimerRef.current = setTimeout(() => setControlsHighlight(false), 30000);
    return () => clearTimeout(highlightTimerRef.current);
  }, []);

  const [workshopDialogOpen, setWorkshopDialogOpen] = useState(false);
  const [workshopData, setWorkshopData] = useState({
    deal: '',
    deadline: '',
    city: '',
    quantity: ''
  });

  const handleOpenScale = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseScale = () => {
    setAnchorEl(null);
  };

  const handleOpenPattern = (event) => {
    setPatternAnchorEl(event.currentTarget);
  };

  const handleClosePattern = () => {
    setPatternAnchorEl(null);
  };

  const handleOpenWorkshopDialog = () => {
    setWorkshopDialogOpen(true);
  };

  const handleCloseWorkshopDialog = () => {
    setWorkshopDialogOpen(false);
  };

  const handleWorkshopDataChange = (field, value) => {
    setWorkshopData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveForWorkshop = () => {
    // Вызываем функцию из App.js для сохранения данных
    if (onSaveForWorkshop) {
      onSaveForWorkshop(workshopData);
    }
    handleCloseWorkshopDialog();
    // Открываем диалог стоимости после сохранения
    if (onOpenPrice) {
      onOpenPrice();
    }
  };

  const open = Boolean(anchorEl);
  const openPattern = Boolean(patternAnchorEl);

  return (
    <AppBar position="static" elevation={0} sx={{ backgroundColor: '#ffffff', borderBottom: '0.0625rem solid #e0e0e0' }}> {/* 1px */}
      {/* Красная полоска сверху */}
      <Box
        sx={{
          height: '0.25rem', // 4px
          background: 'linear-gradient(90deg, #d32f2f 0%, #e53935 50%, #d32f2f 100%)',
        }}
      />
      <MuiToolbar sx={{ flexWrap: 'nowrap', gap: 0.5, minHeight: '4rem', height: '4rem' }}> {/* 64px */}
        {onGoHome && (
          <IconButton onClick={onGoHome} size="small" sx={{ mr: 0.5, color: '#757575' }}>
            <HomeIcon />
          </IconButton>
        )}
        <Button size="small" onClick={onNewDrawing} sx={{ color: '#212121' }}>Новый</Button>
        <Button size="small" onClick={onLoadDrawing} sx={{ color: '#212121' }}>Открыть</Button>
        <Button
          size="small"
          onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
          sx={{ color: '#212121' }}
        >
          Сохранить как
          <Box component="span" sx={{ color: '#d32f2f', mx: 0.3 }}>/</Box>
          <Box component="span" sx={{ color: '#d32f2f' }}>Рассчитать</Box>
        </Button>
        <Menu
          anchorEl={saveMenuAnchor}
          open={Boolean(saveMenuAnchor)}
          onClose={() => setSaveMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => { if (isLoggedIn) { onSaveDrawing(); setSaveMenuAnchor(null); } else { setSaveMenuAnchor(null); setGuestSnackbar(true); } }}
            sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }}
          >
            <ListItemIcon><DescriptionIcon fontSize="small" sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }} /></ListItemIcon>
            <ListItemText>Чертёж ProxythermCAD</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => { if (isLoggedIn) { onSaveAsPdf(); setSaveMenuAnchor(null); } else { setSaveMenuAnchor(null); setGuestSnackbar(true); } }}
            sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }}
          >
            <ListItemIcon><PdfIcon fontSize="small" sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }} /></ListItemIcon>
            <ListItemText>Чертёж PDF</ListItemText>
          </MenuItem>
          {onSaveAsDwg && (
            <MenuItem
              onClick={async () => {
                setSaveMenuAnchor(null);
                if (isLoggedIn) await onSaveAsDwg();
                else setGuestSnackbar(true);
              }}
              sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }}
            >
              <ListItemIcon><StraightenIcon fontSize="small" sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }} /></ListItemIcon>
              <ListItemText>Чертёж DWG</ListItemText>
            </MenuItem>
          )}
          {onSaveSpecification && (
            <MenuItem
              onClick={async () => {
                setSaveMenuAnchor(null);
                if (isLoggedIn) await onSaveSpecification();
                else setGuestSnackbar(true);
              }}
              sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }}
            >
              <ListItemIcon><ListAltIcon fontSize="small" sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }} /></ListItemIcon>
              <ListItemText>Спецификация PDF</ListItemText>
            </MenuItem>
          )}
          {onSaveAsSTEP && (
            <MenuItem
              onClick={async () => {
                setSaveMenuAnchor(null);
                if (isLoggedIn) await onSaveAsSTEP();
                else setGuestSnackbar(true);
              }}
              sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }}
            >
              <ListItemIcon><ViewInArIcon fontSize="small" sx={{ color: isLoggedIn ? 'inherit' : '#bdbdbd' }} /></ListItemIcon>
              <ListItemText>3D модель STEP</ListItemText>
            </MenuItem>
          )}
          {isAdmin && (onSaveCuttingModels || onSaveAsIGES || onSaveAsDXF) && (
            <MenuItem onClick={async () => {
              setSaveMenuAnchor(null);
              if (onSaveCuttingModels) {
                await onSaveCuttingModels();
                return;
              }
              let igesSuccess = false;
              if (onSaveAsIGES) {
                igesSuccess = await onSaveAsIGES();
              }
              if (onSaveAsDXF && igesSuccess !== false) {
                await new Promise(resolve => setTimeout(resolve, 500));
                await onSaveAsDXF();
              }
            }}>
              <ListItemIcon><ViewInArIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Модели для резки</ListItemText>
            </MenuItem>
          )}
          {onRequestQuote && [
            isLoggedIn && <Box key="divider" sx={{ height: '0.0625rem', background: '#f0f0f0', mx: 1, my: 0.5 }} />,
            <MenuItem key="quote" onClick={() => { setSaveMenuAnchor(null); onRequestQuote(); }}
              sx={{ color: '#d32f2f' }}
            >
              <ListItemIcon><SendIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
              <ListItemText>Отправить запрос на расчёт в Proxytherm</ListItemText>
            </MenuItem>
          ]}
        </Menu>
        <Box sx={{ height: '1.5rem', borderLeft: '0.0625rem solid #e0e0e0', mx: 0.5 }} /> {/* 24px height, 1px border */}
        {onBackToAutoConstructor && (
          <Button size="small" onClick={onBackToAutoConstructor} sx={{ color: '#212121' }}>
            Параметры
          </Button>
        )}
        <Button size="small" onClick={handleOpenPattern} sx={{ color: '#212121' }}>Массив</Button>
        <Button size="small" onClick={handleOpenScale} sx={{ color: '#212121' }}>Масштаб</Button>
        {isAdmin && (
          <Button size="small" onClick={onOpenPrice} sx={{ color: '#212121' }}>Стоимость</Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Box 
          sx={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5 
          }}
        >
          <Box
            component="img"
            src="/proxytherm-logo.png"
            alt="ProxyTherm"
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{
              height: '2rem', // 32px
              width: 'auto',
            }}
          />
          <Typography variant="h6" sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#212121' }}>
            ProxythermCAD
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => { setControlsOpen(true); setControlsHighlight(false); clearTimeout(highlightTimerRef.current); }}
          startIcon={<KeyboardAltIcon sx={{ fontSize: '1rem' }} />}
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: '0.375rem',
            px: 1.5,
            transition: 'all 0.3s ease',
            ...(controlsHighlight ? {
              color: '#fff',
              background: 'linear-gradient(135deg, #d32f2f, #e53935)',
              border: '0.0625rem solid #c62828',
              boxShadow: '0 0 0 0 rgba(0,0,0,1)',
              animation: 'controlsPulse 1.5s ease-in-out infinite',
              '@keyframes controlsPulse': {
                '0%, 100%': { boxShadow: '0 0 0.1rem 0.1rem rgba(0,0,0,1)' },
                '50%': { boxShadow: '0 0 0.5rem 0.5rem rgba(0,0,0,0)' },
              },
              '&:hover': { background: 'linear-gradient(135deg, #c62828, #d32f2f)', borderColor: '#b71c1c' },
            } : {
              color: '#424242',
              border: '0.0625rem solid #e0e0e0',
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bdbdbd' },
            }),
          }}
        >
          Управление
        </Button>
        <Tooltip title={getManualLabel(isAdmin)} placement="bottom" arrow>
          <IconButton
            size="small"
            onClick={() => openUserManual(isAdmin)}
            sx={{ color: '#757575', '&:hover': { color: '#d32f2f', backgroundColor: 'rgba(211,47,47,0.06)' } }}
          >
            <MenuBookIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        </Tooltip>
        {isAdmin && workshopInfo && (
          <Button 
            size="small" 
            onClick={onSendToWorkshop}
            variant="contained"
            color="primary"
            sx={{ 
              ml: 1,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(211,47,47, 0.7)' },
                '50%': { boxShadow: '0 0 0 0.25rem rgba(211,47,47, 0)' },
              },
            }}
          >
            Отправить в цех
          </Button>
        )}
        {isAdmin && (
          <Button 
            size="small" 
            onClick={handleOpenWorkshopDialog} 
            variant="outlined"
            color="primary"
            sx={{ ml: workshopInfo ? 0.5 : 1 }}
          >
            {workshopInfo ? 'Изменить оформление' : 'Оформить для цеха'}
          </Button>
        )}
        {isAdmin && workshopInfo && onSaveForWorkshop && (
          <Button 
            size="small" 
            onClick={() => onSaveForWorkshop(null)} 
            variant="text"
            sx={{ ml: 0.5, color: '#757575' }}
          >
            Убрать оформление
          </Button>
        )}
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseScale}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, width: 300 }}>
            <Typography variant="subtitle2" gutterBottom>Масштаб общий: {Math.round((zoom || 1) * 100)}%</Typography>
            <Slider
              value={zoom}
              min={0.25}
              max={3}
              step={0.05}
              onChange={(e, value) => setZoom(value)}
            />
            <Box sx={{ height: 8 }} />
            <Typography variant="subtitle2" gutterBottom>Масштаб текста размеров: {Math.round((textZoom || 1) * 100)}%</Typography>
            <Slider
              value={textZoom}
              min={0.25}
              max={3}
              step={0.05}
              onChange={(e, value) => setTextZoom(value)}
            />
            <Box sx={{ height: 8 }} />
            <Typography variant="subtitle2" gutterBottom>Масштаб текста позиций: {Math.round((positionTextZoom || 1) * 100)}%</Typography>
            <Slider
              value={positionTextZoom}
              min={0.25}
              max={3}
              step={0.05}
              onChange={(e, value) => setPositionTextZoom(value)}
            />
          </Box>
        </Popover>
        <Popover
          open={openPattern}
          anchorEl={patternAnchorEl}
          onClose={handleClosePattern}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>Линейный массив</Typography>
            <TextField
              label="Количество"
              type="number"
              size="small"
              value={patternCount}
              onChange={(e) => setPatternCount(Math.max(1, parseInt(e.target.value || '0', 10)))}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Шаг по X"
              type="number"
              size="small"
              value={patternDx}
              onChange={(e) => setPatternDx(parseFloat(e.target.value || '0'))}
            />
            <TextField
              label="Шаг по Y"
              type="number"
              size="small"
              value={patternDy}
              onChange={(e) => setPatternDy(parseFloat(e.target.value || '0'))}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (typeof onApplyLinearPattern === 'function') {
                  onApplyLinearPattern({ count: Number(patternCount) || 1, dx: Number(patternDx) || 0, dy: Number(patternDy) || 0 });
                }
                handleClosePattern();
              }}
            >
              Применить к выделенным
            </Button>
          </Box>
        </Popover>
        {/* ===== ДИАЛОГ УПРАВЛЕНИЯ ===== */}
        <Dialog
          open={controlsOpen}
          onClose={() => setControlsOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '0.75rem',
              background: '#fafafa',
              overflow: 'hidden',
              boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)',
            }
          }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{
              background: '#fff',
              borderBottom: '0.0625rem solid #e0e0e0',
              px: 3, py: 1.75,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: '2.25rem', height: '2.25rem',
                  borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #d32f2f, #e53935)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <KeyboardAltIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#212121', lineHeight: 1.2 }}>
                    Управление
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1 }}>
                    ProxythermCAD — справочник горячих клавиш
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setControlsOpen(false)} size="small" sx={{ color: '#9e9e9e', '&:hover': { color: '#212121', background: '#f5f5f5' } }}>
                ✕
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 2.5, background: '#fafafa' }}>
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.5,
              background: '#fff',
              border: '0.0625rem solid #e8e8e8',
              borderLeft: '0.25rem solid #d32f2f',
              borderRadius: '0.5rem',
              px: 2, py: 1.5,
              mb: 2.5,
              boxShadow: '0 0.0625rem 0.25rem rgba(0,0,0,0.05)',
            }}>
              <DescriptionIcon sx={{ color: '#d32f2f', fontSize: '1.1rem', mt: '0.1rem', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#212121', mb: 0.3 }}>
                  Чертёж ProxythermCAD (.ptCAD)
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#616161', lineHeight: 1.5 }}>
                  Внутренний формат программы. При нажатии «Сохранить» вам будет предложено выбрать способ:
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  <Box component="li" sx={{ fontSize: '0.75rem', color: '#616161', lineHeight: 1.5 }}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#424242' }}>На устройство</Box> — скачать файл <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#424242' }}>.ptCAD</Box> локально на компьютер
                  </Box>
                  <Box component="li" sx={{ fontSize: '0.75rem', color: '#616161', lineHeight: 1.5, listStylePosition: 'outside' }}>
                    <Box component="span" sx={{ fontWeight: 600, color: '#424242' }}>В облако</Box>
                    {' — '}сохранить копию чертежа в облако и добавить её в вашу историю (нужна авторизация).
                    <Typography component="div" sx={{ fontSize: '0.72rem', color: '#9e9e9e', lineHeight: 1.45, mt: 0.45, pl: 0 }}>
                      История доступна на главной странице: нажмите на блок с вашим аккаунтом вверху справа и откройте пункт «История чертежей» — там же профиль и другие настройки аккаунта.
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e', lineHeight: 1.5, mt: 0.75 }}>
                  Только файл <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#424242' }}>.ptCAD</Box> можно открыть через «Открыть» и продолжить редактирование. PDF и другие форматы предназначены только для передачи и печати.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>

              {/* НАВИГАЦИЯ */}
              <ControlSection icon={<MouseIcon />} title="Навигация и вид" color="#42a5f5">
                <ControlRow keys={['Средняя кнопка мыши (зажатая)']} desc="Перемещение чертежа по рабочей области" />
                <ControlRow keys={['M (Ь)', 'Колесо мыши']} desc="Масштаб всего чертежа" />
                <ControlRow keys={['< (Б)', 'Колесо мыши']} desc="Масштаб текста размеров" />
                <ControlRow keys={['> (Ю)', 'Колесо мыши']} desc="Масштаб текста позиций" />
              </ControlSection>

              {/* ВЫДЕЛЕНИЕ И ОБЪЕКТЫ */}
              <ControlSection icon={<TouchAppIcon />} title="Выделение и объекты" color="#66bb6a">
                <ControlRow keys={['Левая кнопка мыши']} desc="Выделить / снять выделение" />
                <ControlRow keys={['Левая кнопка мыши', 'Ctrl']} desc="Добавить к выделению" />
                <ControlRow keys={['Delete']} desc="Удалить выделенные объекты" />
                <ControlRow keys={['L', 'Клик']} desc="Заблокировать от перемещения" />
                <ControlRow keys={['Ctrl', 'Z']} desc="Отменить последнее действие" />
                <ControlRow keys={['Ctrl', 'Y']} desc="Повторить отменённое" />
              </ControlSection>

              {/* ПРИВЯЗКИ */}
              <ControlSection icon={<LayersIcon />} title="Привязки и соединения" color="#ffa726">
                <ControlRow keys={['P']} desc="Переключить: выкл. → точки → стенки → выкл." />
                <ControlRow keys={['Левая кнопка мыши', 'точка привязки']} desc="Режим точек: клик по маркеру на цилиндре или фитинге — первая или вторая точка цепочки между объектами." />
                <ControlRow keys={['Левая кнопка мыши', 'стенка трубы']} desc="Режим стенок: клик по ребру/стенке сегмента на одном объекте, затем по стенке на другом — выравнивание по граням." />
                <ControlRow keys={['Ctrl', 'Клик на объект']} desc="Прикрепить текст позиции (подпись) к объекту на чертеже." />
              </ControlSection>

              {/* РИСОВАНИЕ */}
              <ControlSection icon={<DrawIcon />} title="Рисование труб" color="#ab47bc">
                <ControlRow keys={['Включить рисование']} desc="Активировать режим рисования контура" />
                <ControlRow keys={['Левая кнопка мыши']} desc="Нарисовать замкнутый контур трубы" />
                <ControlRow keys={['Правая кнопка мыши на объект']} desc="Выбрать трубу и добавить в расчёт" />
                <ControlRow keys={['Esc']} desc="Выйти из режима рисования" />
                <Box sx={{
                  mt: 1,
                  mb: 0.25,
                  px: 1.25,
                  py: 0.9,
                  borderRadius: '0.45rem',
                  background: 'linear-gradient(90deg, #ab47bc1f 0%, #ab47bc08 100%)',
                  border: '0.0625rem solid #ce93d8',
                  borderLeft: '0.22rem solid #ab47bc',
                }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#424242', lineHeight: 1.45, fontWeight: 500 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#7b1fa2' }}>Важно:</Box>
                    {' '}функция не работает при активной кнопке{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: '#212121' }}>«Включить размеры»</Box>.
                  </Typography>
                </Box>
              </ControlSection>

              {/* РАЗМЕРНЫЕ ЛИНИИ */}
              <ControlSection icon={<StraightenIcon />} title="Размерные линии" color="#26c6da">
                <ControlRow keys={['Включить размеры']} desc="Расстановка размеров — указать стенки или точки" />
                <ControlRow keys={['Shift', 'Левая кнопка мыши (зажатая)']} desc="Перемещение текста размерной линии" />
                <ControlRow keys={['Левая кнопка мыши (по размеру)']} desc="Выделить и редактировать размер" />
              </ControlSection>

              {/* МАСШТАБ И НАСТРОЙКИ */}
              <ControlSection icon={<ZoomInIcon />} title="Масштаб и настройки" color="#ef5350">
                <ControlRow keys={['Масштаб']} desc="Открыть панель масштабирования чертежа и текста" />
                <ControlRow keys={['Массив']} desc="Линейное копирование выделенных объектов" />
                <ControlRow keys={['Параметры']} desc="Вернуться к настройкам автоконструктора" />
                <ControlRow keys={['Новый']} desc="Создать новый чертёж" />
                <ControlRow keys={['Открыть']} desc="Загрузить сохранённый чертёж" />
                <ControlRow keys={['Сохранить как']} desc="Экспорт: чертёж .ptCAD, PDF, DWG, STEP, спецификация" />
              </ControlSection>

            </Box>
          </DialogContent>

          <Box sx={{
            background: '#fff',
            borderTop: '0.0625rem solid #e0e0e0',
            px: 3, py: 1.25,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#bdbdbd' }}>
              Горячие клавиши актуальны для режима чертежа
              {' · '}
              <Box
                component="button"
                type="button"
                onClick={() => openUserManual(isAdmin)}
                sx={{
                  p: 0,
                  border: 'none',
                  background: 'none',
                  color: '#d32f2f',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {getManualLabel(isAdmin)}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  localStorage.setItem(controlsSeenKey, 'true');
                  setControlsOpen(false);
                  setControlsHighlight(false);
                  clearTimeout(highlightTimerRef.current);
                }}
                sx={{ fontSize: '0.75rem', color: '#9e9e9e', '&:hover': { color: '#424242', background: '#f5f5f5' } }}
              >
                Больше не показывать
              </Button>
              <Button
                size="small"
                onClick={() => setControlsOpen(false)}
                variant="outlined"
                sx={{ fontSize: '0.75rem', borderColor: '#e0e0e0', color: '#616161', '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' } }}
              >
                Закрыть
              </Button>
            </Box>
          </Box>
        </Dialog>

        <Dialog open={workshopDialogOpen} onClose={handleCloseWorkshopDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Сохранить для цеха</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Сделка:"
                fullWidth
                value={workshopData.deal}
                onChange={(e) => handleWorkshopDataChange('deal', e.target.value)}
              />
              <TextField
                label="Крайний срок:"
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={workshopData.deadline}
                onChange={(e) => handleWorkshopDataChange('deadline', e.target.value)}
              />
              <TextField
                label="Город:"
                fullWidth
                value={workshopData.city}
                onChange={(e) => handleWorkshopDataChange('city', e.target.value)}
              />
              <TextField
                label="Количество:"
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                value={workshopData.quantity}
                onChange={(e) => handleWorkshopDataChange('quantity', e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseWorkshopDialog}>Отмена</Button>
            <Button onClick={handleSaveForWorkshop} variant="contained" color="primary">
              Сохранить
            </Button>
          </DialogActions>
        </Dialog>
      </MuiToolbar>

      {/* Уведомление для гостя при попытке сохранить */}
      <Snackbar
        open={guestSnackbar}
        autoHideDuration={4000}
        onClose={() => setGuestSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setGuestSnackbar(false)}
          severity="info"
          variant="filled"
          sx={{ borderRadius: '0.6rem', fontSize: '0.85rem', alignItems: 'center' }}
        >
          Эта функция доступна только зарегистрированным пользователям
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

export default Toolbar;
